import React, { useState } from "react";
import SidebarTopbar from "../../components/layouts/layout";
import { FaExchangeAlt } from "react-icons/fa";
import { HiOutlineDocumentArrowUp } from "react-icons/hi2";
import { BsCloudUpload, BsFileText, BsPlayCircle, BsLightningCharge, BsShieldCheck } from "react-icons/bs";
import "./uploadfile.css";
import { useNavigate, useLocation } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

function UploadPublication() {
  // Removed the unused file state
  const [alerts, setAlerts] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [publicationId, setPublicationId] = useState(null);
  const [publicationTitle, setPublicationTitle] = useState("");
  const [showOptions, setShowOptions] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem("authToken");
  const navigate = useNavigate();
  const location = useLocation();

  const targetPage = location.state?.target;

  const handleFileUpload = async () => {
    if (!publicationId) {
      createAlert("No publication available. Please upload a file first.", "error");
      return;
    }

    setIsGenerating(true);

    try {
      // Call generateGraphs API
      const formData = new FormData();
      formData.append("publication_id", publicationId);

      const response = await fetch(`${BASE_URL}/generateGraphs`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData,
      });

      if (response.ok) {
        createAlert("Graphs generated successfully!", "success");

        // Navigate to the target page after successful API call
        if (targetPage === "pubChat") {
          navigate(`/pubChat/${publicationId}`);
        } else {
          // Default to publicationView (assets page) for PubGen flow
          navigate(`/publicationView/${publicationId}`);
        }
      } else {
        createAlert("Failed to generate graphs. Please try again.", "error");
      }
    } catch (error) {
      console.error("Error generating graphs:", error);
      createAlert("Error occurred while generating graphs. Please try again.", "error");
    } finally {
      setIsGenerating(false);
    }
  };

  const closeAlert = (id) => {
    setAlerts((a) => a.filter((alert) => alert.id !== id));
  };

  const createAlert = (message, severity) => {
    const id = Date.now();
    setAlerts((prev) => [...prev, { id, message, severity }]);
    setTimeout(() => closeAlert(id), 5000);
  };

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    // Reset the input value to allow re-uploading the same file
    event.target.value = null;

    // Removed setFile(selectedFile);
    setAlerts([]);
    setIsUploading(true);

    if (selectedFile.size > 20 * 1024 * 1024) {
      createAlert("File size is too large. Please upload a file below 20MB.", "warning");
      setIsUploading(false);
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);

    if (!token) {
      alert("You need to log in first to get a token.");
      setIsUploading(false);
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/uploadPublication`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (response.status === 413) {
        createAlert("File size is too large. Please upload a file below 20MB.", "warning");
      } else if (response.status === 500) {
        createAlert("Sorry it's us. Rectifying. Please try after some time.", "error");
      } else if (response.status === 400) {
        createAlert("Oops! This doesn’t look like a medical publication.\nTo ensure accurate analysis, please upload a medical or scientific publication. Try again!", "error");
      } else if (response.ok) {
        const data = await response.json();
        createAlert("File uploaded successfully!", "success");
        setPublicationId(data.id);
        setPublicationTitle(data.title);
        setShowOptions(true);

        // 👇 Add start_chat call here
        const chatFormData = new FormData();
        chatFormData.append("publicationId", data.id);

        try {
          const chatResponse = await fetch(`${BASE_URL}/start_chat`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: chatFormData,
          });

          if (!chatResponse.ok) {
            createAlert("Chat initialization failed. Please try again.", "warning");
          }
        } catch (err) {
          console.error("Error during chat initialization:", err);
          createAlert("Chat initialization failed. Please try again.", "warning");
        }
        // ☝️ End of start_chat call
      } else {
        createAlert("Upload failed. Please try again.", "error");
      }
    } catch (error) {
      console.error("Error during file upload:", error);
      createAlert("Error occurred. Please try again.", "error");
    } finally {
      setIsUploading(false);
    }
  };

  const triggerFileInput = (e) => {
    if (isUploading) return;
    e.stopPropagation();
    document.getElementById("fileInput").click();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileChange({ target: { files: [droppedFile] } });
    }
  };

  const handleReplace = (e) => {
    e.stopPropagation();
    document.getElementById("replaceFileInput").click();
  };

  const handleReplaceFileChange = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    // Reset the input value to allow re-uploading the same file
    event.target.value = null;

    // Removed setFile(selectedFile);
    setAlerts([]);
    setIsUploading(true);

    if (selectedFile.size > 20 * 1024 * 1024) {
      createAlert("File size is too large. Please upload a file below 20MB.", "warning");
      setIsUploading(false);
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("publication_id", publicationId);

    if (!token) {
      alert("You need to log in first to get a token.");
      setIsUploading(false);
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/replacePublication`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (response.status === 413) {
        createAlert("File size is too large. Please upload a file below 20MB.", "warning");
      } else if (response.status === 500) {
        createAlert("Sorry it's us. Rectifying. Please try after some time.", "error");
      } else if (response.status === 400) {
        createAlert("Oops! This doesn’t look like a medical publication.\nTo ensure accurate analysis, please upload a medical or scientific publication. Try again!", "error");
      } else if (response.ok) {
        const data = await response.json();
        createAlert("File replaced successfully!", "success");
        setPublicationId(data.id);
        setPublicationTitle(data.title);
        setShowOptions(true);
      } else {
        createAlert("Replace failed. Please try again.", "error");
      }
    } catch (error) {
      console.error("Error during file replace:", error);
      createAlert("Error occurred. Please try again.", "error");
    } finally {
      setIsUploading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!publicationId) return;
    const formData = new FormData();
    formData.append("publicationId", publicationId);

    try {
      const response = await fetch(`${BASE_URL}/deletePublication`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!response.ok) throw new Error("Failed to delete publication");
      await response.json();
    } catch (error) {
      console.error("Error deleting publication:", error);
      createAlert("Error deleting publication. Please try again.", "error");
    } finally {
      // Removed setFile(null);
      setAlerts([]);
      setIsUploading(false);
      setPublicationId(null);
      setPublicationTitle("");
      setShowOptions(false);
    }
  };

  const handleCancel = () => handleConfirmDelete();

  return (
    <SidebarTopbar>
      <div className="upload-wrapper">
        <div className="upload-container">
        {alerts.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: "70px",
              right: "20px",
              width: "300px",
              zIndex: 1000,
            }}
          >
            <Stack spacing={2}>
              {alerts.map((alert) => (
                <Alert
                  key={alert.id}
                  severity={alert.severity}
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => closeAlert(alert.id)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  {alert.message}
                </Alert>
              ))}
            </Stack>
          </div>
        )}

        <h2 className="upload-title">Upload Publication</h2>
        <p className="upload-description">
          Upload PDF / DOCX and PubVision AI will read it. You'll be able to
          generate video, presentation, conference-ready poster, and
          audio summary in minutes.
        </p>

        {!showOptions && (
          <>
            <div
              className={`upload-box ${isUploading ? "uploading disabled" : ""} ${isDragging ? "dragging" : ""}`}
              onClick={triggerFileInput}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              style={isUploading ? { pointerEvents: "none" } : {}}
            >
              <input
                type="file"
                id="fileInput"
                accept=".pdf,.docx"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              <div className="icon-container">
                <BsCloudUpload className="upload-icon" />
              </div>
              <h3 className="upload-instructions">Drag and drop your file here</h3>
              <p className="upload-or">or</p>
              <button
                className="upload-button"
                onClick={triggerFileInput}
                disabled={isUploading}
                style={isUploading ? { cursor: "not-allowed" } : {}}
              >
                {isUploading ? (
                  <span className="uploading-text">
                    <span className="upload-spinner"></span>
                    Extracting your publication...
                  </span>
                ) : (
                  <>
                    <HiOutlineDocumentArrowUp className="button-icon" /> Choose File
                  </>
                )}
              </button>
              <p className="upload-limit">Supports PDF and DOCX files<br/>Max file size: 20 MB</p>
            </div>

            <div className="features-container">
              <div className="feature-card">
                <div className="feature-icon bg-blue">
                  <BsFileText />
                </div>
                <div className="feature-text">
                  <h4>Smart Processing</h4>
                  <p>AI reads and understands your content</p>
                </div>
              </div>
              <div className="feature-card">
                <div className="feature-icon bg-green">
                  <BsPlayCircle />
                </div>
                <div className="feature-text">
                  <h4>Multi-format Output</h4>
                  <p>Generate Video, PPT, Poster, Carousel, Post</p>
                </div>
              </div>
              <div className="feature-card">
                <div className="feature-icon bg-purple">
                  <BsLightningCharge />
                </div>
                <div className="feature-text">
                  <h4>Fast & Easy</h4>
                  <p>Get your summary in just minutes</p>
                </div>
              </div>
            </div>

            <div className="security-banner">
              <BsShieldCheck className="security-icon" />
              <span>Your data is safe and secure. We never share your files with anyone.</span>
            </div>
          </>
        )}

        <input
          type="file"
          id="replaceFileInput"
          accept=".pdf,.docx"
          onChange={handleReplaceFileChange}
          style={{ display: "none" }}
        />

        {showOptions && (
          <>
            <div className="uploaded-details">
              <HiOutlineDocumentArrowUp className="uploaded-icon" />
              <span className="uploaded-filename">{publicationTitle}</span>
              <button className="replace-button" onClick={handleReplace} disabled={isUploading}>
                {isUploading ? "Uploading..." : <><FaExchangeAlt className="replace-icon" /> Replace</>}
              </button>
            </div>
            <div className="bottom-buttons">
              <button className="cancel-button" onClick={handleCancel}>
                Cancel
              </button>
              <button
                className="next-button"
                onClick={handleFileUpload}
                disabled={isGenerating || isUploading}
              >
                {isGenerating ? (
                  <span className="uploading-text">
                    <span className="upload-spinner"></span>
                    Uploading...
                  </span>
                ) : "Next"}
              </button>
            </div>
          </>
        )}


        </div>
      </div>
    </SidebarTopbar>
  );
}

export default UploadPublication;